import { FiX } from 'react-icons/fi';
import { FaInstagram, FaFacebookF } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { HiOutlineChatBubbleOvalLeft } from 'react-icons/hi2';
import { toast } from '../../utils/toast';
import { copyTextToClipboard } from '../../utils/copyToClipboard';

interface ShareEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string;
  shareTitle: string;
}

export function ShareEventModal({ isOpen, onClose, shareUrl, shareTitle }: ShareEventModalProps) {
  if (!isOpen) return null;

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);

  const handleCopyLink = async () => {
    const copied = await copyTextToClipboard(shareUrl);
    if (copied) {
      toast.success('Link copied to clipboard');
    } else {
      toast.error('Could not copy link');
    }
  };

  const handleInstagramShare = async () => {
    const copied = await copyTextToClipboard(shareUrl);
    if (copied) {
      toast.success('Link copied — paste it into your Instagram story or DM');
    } else {
      toast.error('Could not copy link');
    }
  };

  const socialLinks = [
    {
      key: 'instagram',
      label: 'Share on Instagram',
      icon: FaInstagram,
      onClick: handleInstagramShare,
    },
    {
      key: 'twitter',
      label: 'Share on X',
      icon: FaXTwitter,
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
    },
    {
      key: 'facebook',
      label: 'Share on Facebook',
      icon: FaFacebookF,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      key: 'messages',
      label: 'Share via Messages',
      icon: HiOutlineChatBubbleOvalLeft,
      href: `sms:&body=${encodedTitle}%20${encodedUrl}`,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[480px] rounded-3xl border border-[#222222] bg-black pt-2 shadow-2xl text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 pt-5 pr-5 pb-4 pl-6">
          <div>
            <h3 className="text-base font-bold">Share Event</h3>
            <p className="text-xs text-zinc-400 mt-0.5">
              Share this event with friends and help spread the word.
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 transition cursor-pointer"
            aria-label="Close"
          >
            <FiX size={16} />
          </button>
        </div>

        <div className="flex flex-col gap-4 pt-4 pr-6 pb-6 pl-6">
          <div className="flex flex-col gap-3 rounded-2xl border border-[#222222] bg-[#111111] p-3">
            <div className="flex items-center gap-3">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="min-w-0 flex-1 rounded-xl border border-[#222222] bg-black px-3.5 py-3 text-xs text-zinc-300 focus:outline-none truncate"
              />
              <button
                onClick={handleCopyLink}
                className="shrink-0 rounded-full bg-white px-7 py-3.5 text-xs font-bold text-black hover:bg-zinc-200 transition cursor-pointer"
              >
                Copy Link
              </button>
            </div>
            <p className="text-center text-xs text-zinc-400">
              Or share directly on your favorite platform
            </p>
          </div>

          <div className="flex items-center justify-center gap-4">
            {socialLinks.map((item) => {
              const Icon = item.icon;
              const className =
                'w-14 h-14 flex items-center justify-center rounded-full border border-[#222222] bg-[#111111] text-white hover:bg-zinc-800 transition cursor-pointer';
              if (item.href) {
                return (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                    aria-label={item.label}
                    title={item.label}
                  >
                    <Icon size={20} />
                  </a>
                );
              }
              return (
                <button
                  key={item.key}
                  onClick={item.onClick}
                  className={className}
                  aria-label={item.label}
                  title={item.label}
                >
                  <Icon size={20} />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
